import React, { useEffect, useState } from "react";
import { API } from "../../../config/api";
import { DELETE, GET, PUT, POST } from "../../../utils/apicalls";
import { useParams } from "react-router-dom";
import { Button, Form, Input, Modal, Select, notification } from "antd";
import "./cart.css";
import Header from "../../../websiteCode/componets/headerwebsite/headerweb";
import FooterWeb from "../../../websiteCode/componets/footerwebsite/footerweb";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import bank from "../../../assets/qr.jpg";

interface Product {
    id: number;
    productTitle: string;
    Price: number;
    productImage1: string;
    clientId: number;
}

interface Variant {
    id: number;
    variantName: string;
    variantprice: number;
}

interface CartItem {
    id: number;
    product: Product;
    variants?: Variant[];
    selectedVariant?: Variant | null;
    quantity: number;
    subtotal: number;
}

function Cart() {
    const [items, setItems] = useState<CartItem[]>([]);
    const { id } = useParams();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    // ✅ Fetch cart + variants
    const fetchCartItems = async () => {
        try {
            const response: any = await GET(API.LISTING_CART_DATA + id, {});
            if (response?.data) {
                const cartData = response.data;

                const itemsWithVariants = await Promise.all(
                    cartData.map(async (item: any) => {
                        try {
                            const variantRes: any = await GET(
                                `${API.GET_BY_PRODUCTID_VARIANT}${item.product.id}`,
                                {}
                            );

                            const variants =
                                Array.isArray(variantRes?.data)
                                    ? variantRes.data
                                    : Array.isArray(variantRes)
                                        ? variantRes
                                        : variantRes?.data?.data || [];

                            const selectedVariant = variants.length > 0 ? variants[0] : null;
                            const price =
                                selectedVariant?.variantprice ?? item.product.Price ?? 0;

                            return {
                                ...item,
                                variants,
                                selectedVariant,
                                quantity: item.quantity ?? 1,
                                subtotal: price * (item.quantity ?? 1),
                            } as CartItem;
                        } catch (error) {
                            console.error("Variant fetch error:", error);
                            const fallbackQty = item.quantity ?? 1;
                            const fallbackPrice = item.product?.Price ?? 0;
                            return {
                                ...item,
                                quantity: fallbackQty,
                                subtotal: fallbackPrice * fallbackQty,
                            } as CartItem;
                        }
                    })
                );

                setItems(itemsWithVariants);
                console.log("✅ Final itemsWithVariants:", itemsWithVariants);
            }
        } catch (error) {
            console.error("Error fetching cart items:", error);
        }
    };

    useEffect(() => {
        fetchCartItems();
    }, [id]);

    // ✅ Total Calculation
    const total = items.reduce((sum, item) => sum + (item.subtotal || 0), 0);

    // ✅ Load Image for PDF
    const loadImage = (src: string): Promise<string> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = src;
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d");
                if (ctx) ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL("image/jpeg"));
            };
        });
    };

    // ✅ PDF Generator
    const generateBookingPDF = async (bookingData: any, products: CartItem[]) => {
        const doc = new jsPDF();
        const titleY = 15;

        doc.setFontSize(18);
        doc.text("Booking Confirmation", 14, titleY);
        doc.setFontSize(11);
        doc.text(`Order Date: ${new Date().toLocaleString()}`, 14, titleY + 8);
        doc.text(`Booking Status: Booked`, 14, titleY + 15);

        doc.text("Buyer Details:", 14, titleY + 25);
        doc.setFontSize(10);
        doc.text(`Name: ${bookingData.Buyername}`, 14, titleY + 32);
        doc.text(`Email: ${bookingData.Buyeremail}`, 14, titleY + 38);
        doc.text(`Mobile: ${bookingData.Buyermobile_number}`, 14, titleY + 44);
        doc.text(`Address: ${bookingData.Buyeraddress}`, 14, titleY + 50);

        autoTable(doc, {
            startY: titleY + 60,
            head: [["#", "Product", "Variant", "Price", "Qty", "Subtotal"]],
            body: products.map((item, index) => [
                index + 1,
                item.product?.productTitle || "-",
                item.selectedVariant?.variantName || "-",
                item.selectedVariant?.variantprice || item.product?.Price || 0,
                item.quantity,
                item.subtotal,
            ]),
        });

        const totalAmount = products.reduce(
            (sum, item) => sum + (item.subtotal || 0),
            0
        );
        const finalY = (doc as any).lastAutoTable?.finalY || 120;

        doc.setFontSize(12);
        doc.text(`Total Amount: ${totalAmount}`, 14, finalY + 10);

        doc.setFontSize(11);
        doc.text("Payment Details:", 14, finalY + 25);
        doc.setFontSize(10);
        doc.text("State Bank Of India", 14, finalY + 32);
        doc.text("A/C: 0317053000009312", 14, finalY + 38);
        doc.text("IFSC: SIBL0000317", 14, finalY + 44);
        doc.text("Google Pay: +91 6235505404", 14, finalY + 50);

        const qrImg = await loadImage(bank);
        doc.addImage(qrImg, "JPEG", 150, finalY + 25, 40, 40);

        doc.setFontSize(10);
        doc.text("Thank you for booking with KeralaPlus!", 14, finalY + 70);
        doc.text("For queries, contact support@KeralaPlus.com", 14, finalY + 76);

        doc.save(`Booking_${bookingData.Buyername}_${Date.now()}.pdf`);
    };

    // ✅ Checkout Submission
    const handleCheckoutSubmit = async (values: any) => {
        setLoading(true);
        try {
            const payload = {
                ...values,
                cartId: Number(id),
                orderdate: new Date().toISOString(),
                bookingstatus: "Booked",
                totalAmount: total,
                items: items.map((i) => ({
                    productId: i.product.id,
                    variantId: i.selectedVariant?.id || null,
                    unitPrice: i.selectedVariant?.variantprice ?? i.product?.Price ?? 0,
                    quantity: i.quantity,
                    subtotal: i.subtotal,
                })),
            };

            const response: any = await POST(API.CREATE_BOOKING_DATA, payload);
            if (response?.status) {
                notification.success({ message: "✅ Order placed successfully!" });
                await generateBookingPDF(values, items);
                localStorage.removeItem("cartId");
                setIsModalOpen(false);
                form.resetFields();
                fetchCartItems();
                window.location.href = "/";
            } else {
                notification.error({ message: "❌ Failed to place order" });
            }
        } catch (error) {
            console.error("Checkout error:", error);
            notification.error({ message: "Something went wrong" });
        } finally {
            setLoading(false);
        }
    };

    // ✅ Remove item
    const handleRemoveItem = async (itemId: number) => {
        try {
            const response: any = await DELETE(API.DELETE_CART_DATA + itemId);
            if (response.status) {
                notification.success({ message: "🗑️ Item removed" });
                fetchCartItems();
            }
        } catch (error) {
            console.error("Remove item error:", error);
        }
    };

    // ✅ Quantity change
    const handleQuantityChange = async (itemId: number, newQty: number) => {
        if (newQty < 1) return;
        try {
            const item = items.find((it) => it.id === itemId);
            if (!item) return;

            const unitPrice =
                item.selectedVariant?.variantprice ?? item.product?.Price ?? 0;
            const newSubtotal = unitPrice * newQty;

            const response: any = await PUT(`${API.UPDATE_CART_ITEM}${itemId}`, {
                quantity: newQty,
                variantId: item.selectedVariant?.id || null,
                price: unitPrice,
                subtotal: newSubtotal,
            });

            if (response?.status) {
                setItems((prev) =>
                    prev.map((it) =>
                        it.id === itemId ? { ...it, quantity: newQty, subtotal: newSubtotal } : it
                    )
                );
            } else {
                notification.error({ message: "Failed to update quantity." });
            }
        } catch (error) {
            console.error("Quantity update error:", error);
            notification.error({ message: "Something went wrong!" });
        }
    };

    // ✅ Variant selection fixed
    const handleVariantChange = async (itemId: number, variantId: number) => {
        try {
            const item = items.find((it) => it.id === itemId);
            if (!item) return;

            const newVariant =
                item.variants?.find((v) => v.id === variantId) || null;
            const price = newVariant?.variantprice ?? item.product?.Price ?? 0;
            const newSubtotal = price * (item.quantity || 1);

            // Update UI instantly
            setItems((prev) =>
                prev.map((it) =>
                    it.id === itemId
                        ? { ...it, selectedVariant: newVariant, subtotal: newSubtotal }
                        : it
                )
            );

            // Update backend
            const response: any = await PUT(`${API.UPDATE_CART_ITEM}${itemId}`, {
                quantity: item.quantity,
                variantId: newVariant?.id || null,
                price,
                subtotal: newSubtotal,
            });

            if (!response?.status) {
                notification.error({ message: "Failed to update variant." });
            }
        } catch (error) {
            console.error("Variant update error:", error);
            notification.error({ message: "Something went wrong!" });
        }
    };
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <Header />
            <div className="cart-container">
                <h2 className="cart-title">🛒 Your Cart</h2>

                {items.length === 0 ? (
                    <p className="empty-cart">Your cart is empty.</p>
                ) : (
                    <>
                        <div className="cart-items">
                            {items.map((item) => (
                                <div key={item.id} className="cart-item">
                                    <img
                                        src={item.product?.productImage1}
                                        alt={item.product?.productTitle}
                                        className="cart-image"
                                    />
                                    <div className="cart-details">
                                        <h3>{item.product?.productTitle}</h3>

                                        {/* ✅ Variant Dropdown */}
                                        {item.variants && item.variants.length > 0 && (
                                            <div className="variant-select">
                                                <label>Select Variant:</label>
                                                <Select
                                                    value={item.selectedVariant?.id ?? undefined}
                                                    onChange={(value) =>
                                                        handleVariantChange(item.id, value)
                                                    }
                                                    style={{ width: 200 }}
                                                    placeholder="Select variant"
                                                >
                                                    {item.variants.map((variant) => (
                                                        <Select.Option
                                                            key={variant.id}
                                                            value={variant.id}
                                                        >
                                                            {variant.variantName} (₹{variant.variantprice})
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </div>
                                        )}

                                        <p className="price">
                                            ₹
                                            {item.selectedVariant?.variantprice ??
                                                item.product?.Price}
                                        </p>

                                        <div className="quantity-control">
                                            <Button
                                                onClick={() =>
                                                    handleQuantityChange(item.id, item.quantity - 1)
                                                }
                                            >
                                                -
                                            </Button>
                                            <span style={{ margin: "0 12px" }}>{item.quantity}</span>
                                            <Button
                                                onClick={() =>
                                                    handleQuantityChange(item.id, item.quantity + 1)
                                                }
                                            >
                                                +
                                            </Button>
                                        </div>

                                        <p>Subtotal: ₹{item.subtotal}</p>
                                        <p className="partner">
                                            Partner ID: {item.product?.clientId ?? "Admin"}
                                        </p>
                                    </div>
                                    <Button danger onClick={() => handleRemoveItem(item.id)}>
                                        Remove
                                    </Button>
                                </div>
                            ))}
                        </div>

                        <div className="cart-summary">
                            <h3>Total: ₹{total}</h3>
                            <Button
                                type="primary"
                                size="large"
                                onClick={() => setIsModalOpen(true)}
                            >
                                Proceed to Checkout
                            </Button>
                        </div>

                        {/* ✅ Booking Modal */}
                        <Modal
                            title="Complete Your Booking"
                            open={isModalOpen}
                            onCancel={() => setIsModalOpen(false)}
                            footer={null}
                        >
                            <Form layout="vertical" form={form} onFinish={handleCheckoutSubmit}>
                                <Form.Item
                                    name="Buyername"
                                    label="Full Name"
                                    rules={[{ required: true, message: "Please enter your name" }]}
                                >
                                    <Input placeholder="Enter your name" />
                                </Form.Item>

                                <Form.Item
                                    name="Buyeremail"
                                    label="Email"
                                    rules={[{ required: true, type: "email", message: "Enter valid email" }]}
                                >
                                    <Input placeholder="Enter your email" />
                                </Form.Item>

                                <Form.Item
                                    name="Buyermobile_number"
                                    label="Mobile Number"
                                    rules={[{ required: true, message: "Enter your mobile number" }]}
                                >
                                    <Input placeholder="Enter your mobile number" />
                                </Form.Item>

                                <Form.Item
                                    name="Buyeraddress"
                                    label="Address"
                                    rules={[{ required: true, message: "Enter your address" }]}
                                >
                                    <Input.TextArea rows={3} placeholder="Enter your address" />
                                </Form.Item>

                                <Form.Item name="Buyermessage" label="Message">
                                    <Input.TextArea rows={2} placeholder="Any message?" />
                                </Form.Item>

                                <Button type="primary" htmlType="submit" block loading={loading}>
                                    Confirm Booking
                                </Button>
                            </Form>
                        </Modal>

                        {/* ✅ Payment Info */}
                        <div className="payment-info">
                            <h3>💳 Payment Information</h3>
                            <img src={bank} alt="QR Code" className="qr-image" />
                            <p><strong>Bank:</strong> State Bank Of India</p>
                            <p><strong>Account No:</strong> 0317053000009312</p>
                            <p><strong>IFSC Code:</strong> SIBL0000317</p>
                            <p><strong>Google Pay:</strong> +91 6235505404</p>
                        </div>
                    </>
                )}
            </div>
            <FooterWeb />
        </>
    );
}

export default Cart;
